import { RANGE_PRESETS, type RangePreset, type RangeSelection } from '@anytracker/core'

/**
 * A range in the URL, so a view of one is bookmarkable (T-402, T-305).
 *
 * Two encodings, both already readable in an address bar:
 *
 *   ?range=last30
 *   ?from=2026-06-01&to=2026-06-30
 *
 * The `from`/`to` pair predates the range control and keeps working, which is
 * the point — a link someone saved before this ticket still opens the range
 * they saved.
 */

function isPreset(value: string): value is RangePreset {
  return RANGE_PRESETS.some((known) => known === value)
}

export function readRangeParams(search: URLSearchParams): RangeSelection | null {
  const preset = search.get('range')
  if (preset !== null && isPreset(preset)) {
    return { kind: 'preset', preset }
  }

  const from = search.get('from')
  const to = search.get('to')
  if (from !== null && to !== null) {
    return { kind: 'custom', fromDay: from, toDay: to }
  }
  return null
}

/** Writes a selection into a copy of the params, clearing the other encoding. */
export function writeRangeParams(
  search: URLSearchParams,
  selection: RangeSelection,
): URLSearchParams {
  const next = new URLSearchParams(search)
  next.delete('range')
  next.delete('from')
  next.delete('to')

  if (selection.kind === 'preset') {
    next.set('range', selection.preset)
  } else {
    next.set('from', selection.fromDay)
    next.set('to', selection.toDay)
  }
  return next
}
